import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { authFetch } from '@/utils/auth-fetch';
import { Search, Package, AlertTriangle } from 'lucide-react';
import { useProductTarget, type CohortFilter } from '@/hooks/useProductTarget';
import { parsePastedIds, type ProductIdKind, type ProductTarget, type TargetOptions } from '@/utils/productTarget';

/**
 * ProductTargetPicker — THE control every product-targeting surface uses (F2).
 *
 * ⛔⛔ IT OWNS THE CHOICE, NOT THE OPERATION. It emits a ProductTarget plus options and stops. The
 * caller decides what to do with them, which is what lets one control serve enrichment,
 * regeneration and research without growing a `mode` prop that each surface then branches on — the
 * shape ProductSelectionModal already has, and the shape that turns a shared component back into
 * three components wearing one name.
 *
 * ⚠️ SEARCH COVERS NAME AND SKU because the SKU is the only field that identifies a product
 * uniquely (F1: names are not unique in a real feed — one BikeInn name covers three products). An
 * operator holding a SKU off a spreadsheet is the common case this exists for.
 *
 * ⚠️ THE PASTE TAB REPORTS WHAT IT DROPPED. Malformed ids and duplicates are surfaced, not silently
 * discarded: pasting 60 lines and being told "58 selected" with no explanation reads as the tool
 * losing two, and the operator has no way to learn which.
 *
 * 🔑 "ALL MATCHING" IS A FILTER, NOT A LIST. Selecting it emits a cohort the SERVER resolves. The
 * count shown next to it is an ESTIMATE for the confirmation sentence and is never sent as a limit
 * — the enumerate-then-send approach it replaces carried a hard-coded limit=500000 and a
 * hand-rebuilt copy of the filter.
 */

export interface PickerProduct {
  id: string;
  /**
   * ⛔⛔ WHICH TABLE `id` CAME FROM. /offering projects COALESCE(kb.id, pfi.id), so `id` alone is
   * ambiguous and the picker CANNOT honestly stamp it with its own `idKind` prop. Dropping this
   * field was the defect: an idKind="kbe" picker emitted feed ids labelled kbe, which is precisely
   * the "success with zero work" the ProductTarget model exists to prevent — reintroduced by the
   * control built to prevent it.
   */
  id_kind?: string | null;
  name: string;
  sku?: string | null;
  category?: string | null;
  is_enriched?: boolean;
}

export interface ProductTargetPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteId: string;
  /** Which table's ids this surface consumes. Travels into the emitted target. */
  idKind: ProductIdKind;
  /** Verb for the confirm button — "Enrich", "Regenerate", "Research". */
  actionLabel: string;
  /** Offer the force toggle. Surfaces without a cache concept omit it. */
  showForce?: boolean;
  /** Wording for the force toggle, since "re-enrich" and "regenerate" are not the same promise. */
  forceLabel?: string;
  /**
   * ⛔⛔ COHORT SELECTION IS OFF BY DEFAULT, AND THE DEFAULT IS THE POINT.
   * A cohort is a FILTER and carries no id kind, so it slips past the mismatch guard entirely: on
   * Motocard a filter matching 8,201 products designates 6,143 the enrichment endpoint cannot take,
   * and nothing in the request says so. toRequestBody now REFUSES a cohort unless the caller states
   * the endpoint resolves it kind-awarely — and no endpoint can state that today
   * (batch-enrich-async does not accept a cohort at all; task-ny7t1n8v5c is the row that gives it
   * the listing's full filter set).
   * ⇒ A surface may opt IN once its endpoint qualifies. Until then the affordance is dark and says
   *   why, because a live control that throws on confirm is worse than one that is honestly absent.
   */
  allowCohortSelection?: boolean;
  onConfirm: (target: ProductTarget, options: TargetOptions) => void | Promise<void>;
  /** Injected in tests; defaults to the real listing endpoint. */
  fetchProducts?: (args: { siteId: string; search: string }) => Promise<{ products: PickerProduct[]; total: number }>;
}

async function defaultFetchProducts({ siteId, search }: { siteId: string; search: string }) {
  const params = new URLSearchParams({ limit: '50' });
  if (search) params.append('search', search);
  const res = await authFetch(`/api/sites/${siteId}/offering?${params}`);
  if (!res.ok) {
    // ⇒ FAIL LOUD. An empty list on a failed fetch is indistinguishable from "no products match",
    // and the operator would conclude their filter was wrong rather than that the call broke.
    throw new Error(`Could not load products (HTTP ${res.status})`);
  }
  const data = await res.json();
  // ⛔⛔ THE COUNT LIVES AT data.pagination.total (business-about.js:1500), NOT data.total.
  // I guessed the field and every test injected a fake fetchProducts, so the ADAPTER — the one piece
  // that has to agree with a real endpoint — was the only part nothing exercised. The consequence
  // was silent and total: `total` resolved to 0, so "Select all N matching" NEVER APPEARED against
  // the real API while the suite was green. Same class as the S1 button that posted
  // `enrichedProductData.product_id`: a mock cannot disagree with the server about a shape.
  const total = data?.pagination?.total;
  if (typeof total !== 'number') {
    // ⇒ FAIL LOUD rather than default to 0. A 0 here does not read as "the shape changed", it reads
    // as "this site has no products", and the select-all affordance simply vanishes with no error.
    throw new Error(
      'Product listing response did not include pagination.total — the select-all count cannot be '
      + 'trusted, so the picker refuses to render a total it would have to guess.'
    );
  }
  return {
    products: (data.products || []).map((p: Record<string, unknown>) => ({
      id: String(p.id),
      // ⇒ Carried, never defaulted. A row without it is refused below rather than assumed targetable.
      id_kind: (p.id_kind as string | null) ?? null,
      name: String(p.name ?? ''),
      sku: (p.sku as string | null) ?? null,
      category: (p.category as string | null) ?? null,
      is_enriched: Boolean(p.is_enriched),
    })),
    total,
  };
}

/**
 * ⛔⛔ THE LISTING MOVES AS ONE VALUE — rows, count and the term that produced them.
 *
 * They were three useStates, and a failed refresh cleared the rows while leaving the count and the
 * applied term behind. The screen then showed an error alert NEXT TO "Select all 14,790 matching",
 * and pressing it emitted the PREVIOUS request's filter — a cohort describing a set the operator
 * could no longer see, sourced from a request that had just failed.
 * 🔑 The instance fix is `setTotal(0)` in the catch. The FRAME fix is that a count without its rows
 * and its term is not a state this component can be in: one value, set in one place, cleared in one
 * place. A fourth field added later inherits the property instead of re-opening the hole.
 */
interface Listing {
  products: PickerProduct[];
  total: number;
  /**
   * The search that produced the rows on screen — NOT what is currently typed. The cohort used the
   * live input, so: load search A, type B WITHOUT submitting, click "Select all 14,790 matching" →
   * the UI shows A's rows and A's count while the emitted filter says B.
   */
  appliedSearch: string;
}

const EMPTY_LISTING: Listing = { products: [], total: 0, appliedSearch: '' };

export function ProductTargetPicker({
  open, onOpenChange, siteId, idKind, actionLabel,
  showForce = false, forceLabel = 'Force re-run (ignore what is already done)',
  allowCohortSelection = false,
  onConfirm, fetchProducts = defaultFetchProducts,
}: ProductTargetPickerProps) {
  const target = useProductTarget({ idKind });
  const { clear: clearTarget } = target;
  const [search, setSearch] = useState('');
  /** Guards against out-of-order responses pairing one request's rows with another's search term. */
  const requestSeq = useRef(0);
  const [listing, setListing] = useState<Listing>(EMPTY_LISTING);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [force, setForce] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteReport, setPasteReport] = useState<{ rejected: string[]; duplicatesRemoved: number; accepted: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /** Synchronous twin of `submitting` — see confirm(): state is too late inside one React batch. */
  const submittingRef = useRef(false);
  /** Current-value twin of `target.isCohort` — see load()'s catch: a closure holds the OLD value. */
  const isCohortRef = useRef(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const { products, total, appliedSearch } = listing;

  /**
   * ⚠️ TAKES THE SEARCH AS AN ARGUMENT rather than closing over it. The first version closed over
   * `search`, which put `load` in the mount effect's dependency array and REFETCHED ON EVERY
   * KEYSTROKE — against a listing that can be 14,790 products wide. Found by a test whose mock ran
   * out of queued responses, which is a better detector than it sounds: a component that fetches
   * once per character exhausts a two-call mock immediately.
   */
  const load = useCallback(async (searchTerm: string) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setLoadError(null);
    try {
      const { products: p, total: t } = await fetchProducts({ siteId, search: searchTerm });
      // ⚠️ A SUPERSEDED RESPONSE IS DISCARDED WHOLE. Two Enter presses can complete out of order, and
      // applying the slower one would pair its rows with the newer term — the same mismatch as the
      // live-input bug, arriving by timing instead of by typing.
      if (seq !== requestSeq.current) return;
      setListing({ products: p, total: t, appliedSearch: searchTerm });
    } catch (err) {
      if (seq !== requestSeq.current) return;
      // ⇒ THE WHOLE LISTING GOES, not just the rows. See Listing's note: a surviving count is what
      // kept "Select all N matching" on screen next to the error, emitting the last filter that
      // worked. The operator is told the load failed and is offered nothing to act on.
      setListing(EMPTY_LISTING);
      setLoadError(err instanceof Error ? err.message : 'Could not load products');
      // ⇒ A COHORT DIES WITH ITS CONTEXT; EXPLICIT IDS DO NOT. A cohort IS the filter — with no rows
      // and no count on screen it describes something invisible. Pasted or ticked ids are
      // self-describing: they name the same products whatever the listing shows, so silently
      // discarding them would be its own lie about what the operator chose.
      //
      // ⛔⛔ THE REF, NOT `target.isCohort` — AND THIS IS THE SECOND TIME IN THIS PR. `load` is a
      // useCallback, so `target.isCohort` here is the value from WHEN THE REQUEST STARTED. Select the
      // cohort while a search is in flight and that closure still says false: the failure clears the
      // listing and leaves an INVISIBLE cohort confirmable — no rows, no count, and a live Enrich
      // button describing a filter nobody can see.
      // 🔑 Identical to the `submitting` guard I had to move to a ref earlier in this same file. A
      // remedy is the likeliest place for the next instance of its own defect, and "read the current
      // value, not the one my closure captured" is the shape to grep for, not the one line.
      if (isCohortRef.current) clearTarget();
    } finally {
      // ⚠️ SUPERSEDED REQUESTS MUST NOT CLEAR `loading` EITHER. This `finally` ran unconditionally, so
      // an older response landing while a newer one was still in flight re-enabled the search button
      // mid-request. I added the seq guard to the two branches above and left the third — a remedy is
      // the likeliest place for the next instance of its own defect.
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [fetchProducts, siteId, clearTarget]);

  /**
   * ⛔⛔ A SESSION IS (open, siteId, idKind), AND EVERY DERIVED VALUE DIES WITH IT.
   *
   * Nothing was reset when the dialog closed or the site changed, so: pick 40 products on site A,
   * close, reopen on site B → the picker lists B's products and confirms A's. Worse, the ids were
   * collected as one kind and re-stamped with the CURRENT `idKind` on the way out, which defeats the
   * mismatch guard in toRequestBody precisely where it was supposed to bite — the target does not
   * arrive holding the wrong kind, it arrives LYING about its kind, so nothing has anything to
   * compare. That is the documented "success with zero work done" failure, reached by a route the
   * guard cannot see.
   *
   * ⚠️ AN IN-FLIGHT RESPONSE FOR THE PREVIOUS SITE IS DISCARDED BY THE RELOAD BELOW, not by anything
   * written here. I had also bumped `requestSeq` in this effect for that — and a mutant proved it
   * changed nothing any test could distinguish, because every session change that leaves the dialog
   * OPEN issues a fresh `load`, and that load bumps the sequence itself; a session change that closes
   * the dialog renders nothing to be stale. Unfalsifiable defensive code reads as protection and is
   * not, so it is gone.
   * ⇒ THE INVARIANT IT DEPENDED ON, stated so a future change cannot quietly break it: EVERY session
   * change that leaves the picker open must issue a load. If a cache or a skip is ever added here,
   * the sequence bump has to come back with it.
   *
   * ⚠️ DEPS ARE THE SESSION KEY ONLY — `load` is read through a ref. With `load` in the array, any
   * caller passing an inline `fetchProducts` would re-create it every render and this effect would
   * wipe the operator's selection on each one.
   */
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; });
  useEffect(() => { isCohortRef.current = target.isCohort; });

  useEffect(() => {
    clearTarget();
    setSearch('');
    setListing(EMPTY_LISTING);
    setLoadError(null);
    setLoading(false);
    setForce(false);
    setPasteText('');
    setPasteReport(null);
    submittingRef.current = false;
    setSubmitting(false);
    setConfirmError(null);
    if (open) void loadRef.current('');
  }, [open, siteId, idKind, clearTarget]);

  const applyPaste = () => {
    const r = parsePastedIds(pasteText, idKind);
    target.setExplicitIds(r.target.ids);
    setPasteReport({ rejected: r.rejected, duplicatesRemoved: r.duplicatesRemoved, accepted: r.target.ids.length });
  };

  // ⇒ ONE definition of "can this row be targeted", used by the checkbox, the bulk button and the
  // count — three places that disagreed would be three chances to emit an id the endpoint refuses.
  const targetableProducts = products.filter((p) => p.id_kind === idKind);
  const untargetableCount = products.length - targetableProducts.length;

  // Built from the APPLIED term, so the cohort always describes the rows and the count on screen.
  const currentFilter: CohortFilter = { siteId, search: appliedSearch || null };

  const confirm = async () => {
    const t = target.toTarget();
    if (!t) return;
    /**
     * ⛔⛔ A REF, NOT THE STATE — and the difference is the whole guard.
     *
     * This read `if (submitting) return;` first, and the test written to prove it works FAILED ON THE
     * REAL CODE: two clicks dispatched in one React batch both saw `submitting === false`, because
     * `setSubmitting(true)` does not update the value the second handler has already closed over.
     * The guard was decorative in precisely the scenario it existed for, and the earlier version of
     * the test could not tell — `fireEvent` flushes React between clicks, so the second one landed on
     * an already-disabled button and never reached the handler at all.
     * ⇒ A ref mutates synchronously, so the second handler in the same batch sees it. `disabled` is
     * the affordance an operator sees; THIS is what actually stops the second paid run over 14,790
     * products, billed in full.
     */
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setConfirmError(null);
    try {
      await onConfirm(t, { force });
      onOpenChange(false);
    } catch (err) {
      // ⛔ THE DIALOG STAYS OPEN AND SAYS WHAT HAPPENED. It used to `await` with no catch under a
      // `void confirm()` call site, so a rejected operation became an unhandled promise: the dialog
      // closed, nothing was queued, and the disappearance READ AS SUCCESS. That is a silent failure
      // in the one place the operator is committing to spend.
      setConfirmError(err instanceof Error ? err.message : `Could not start ${actionLabel.toLowerCase()}`);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" /> Choose products to {actionLabel.toLowerCase()}
          </DialogTitle>
          <DialogDescription>
            Search by product name or SKU, or paste a list of IDs.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="browse">
          <TabsList>
            <TabsTrigger value="browse">Browse</TabsTrigger>
            <TabsTrigger value="paste">Paste a list</TabsTrigger>
          </TabsList>

          <TabsContent value="browse" className="space-y-3">
            <div className="flex gap-2">
              <Input
                aria-label="Search by product name or SKU"
                placeholder="Search by product name or SKU…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void load(search); }}
              />
              {/* ⚠️ NAMED: an icon-only button is unreachable by name to a screen reader AND to a
                  test, and the first version of the superseded-response test had to identify it by
                  poking at the SVG class. A control a test can only find by its decoration is a
                  control an operator using assistive tech cannot find at all. */}
              <Button aria-label="Search" variant="outline" onClick={() => void load(search)} disabled={loading}>
                <Search className="h-4 w-4" />
              </Button>
            </div>

            {loadError && (
              <div role="alert" className="flex items-start gap-2 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4 mt-0.5" />
                <span>{loadError}</span>
              </div>
            )}

            <div className="flex items-center gap-2">
              {/* ⇒ Counts and selects only what this endpoint can consume. "Select these 50" that
                  quietly includes 38 untargetable rows is the same lie as the disabled checkbox
                  being clickable — one bulk, one individual. */}
              <Button
                size="sm"
                variant="outline"
                onClick={() => target.selectVisible(targetableProducts.map((p) => p.id))}
                disabled={targetableProducts.length === 0}
              >
                Select these {targetableProducts.length}
              </Button>
              {/**
                * ⛔⛔ COHORT SELECTION IS DARK ON PURPOSE — no consumer can resolve it kind-awarely yet.
                * A cohort is a FILTER and carries no id kind, so it slips past the mismatch guard
                * entirely: on Motocard a filter matching 8,201 products designates 6,143 that the
                * enrichment endpoint cannot take, and nothing in the request says so. toRequestBody
                * now REFUSES a cohort unless the caller states the endpoint resolves it kind-awarely,
                * and no endpoint can state that today — batch-enrich-async does not accept a cohort
                * at all (task-ny7t1n8v5c is the row that gives it the listing's full filter set).
                * ⇒ Offering the button and throwing on confirm would be a live control that lies. A
                *   dark affordance that says WHY beats a live one that fails at the end.
                */}
              {total > products.length && (allowCohortSelection ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => target.selectMatchingFilter(currentFilter, total)}
                >
                  Select all {total.toLocaleString()} matching
                </Button>
              ) : (
                <span className="text-xs text-muted-foreground">
                  Selecting all {total.toLocaleString()} matching is not available yet — this action
                  cannot yet tell which of them it is able to process. Narrow the search, or use
                  “Select these {targetableProducts.length}”.
                </span>
              ))}
              {!target.isEmpty && (
                <Button size="sm" variant="ghost" onClick={target.clear}>Clear</Button>
              )}
            </div>

            {untargetableCount > 0 && (
              // ⇒ Said out loud. A row that is present but unselectable, with no reason given, reads
              // as a broken control; the operator needs to know it is a FEED-INGESTION state, which
              // is a different next action from "retry" or "it is already done".
              <div role="status" className="text-sm text-muted-foreground">
                {untargetableCount} of these {products.length} product(s) cannot be targeted by this
                action — they are not in the knowledge base yet, so their feed has not been ingested.
              </div>
            )}

            {target.isCohort && (
              // ⇒ Said out loud, because the difference between "all matching" and "the rows I can
              // see" is invisible otherwise — and it is the difference between 14,790 and 50.
              <div role="status" className="text-sm text-muted-foreground">
                All {target.size?.toLocaleString() ?? 'matching'} products matching the current search
                are selected. Individual rows cannot be unticked while this is active — use
                “Select these {products.length}” to narrow to the visible ones.
              </div>
            )}

            <ScrollArea className="h-64 border rounded-md">
              {products.map((p) => (
                <label key={p.id} className="flex items-center gap-3 px-3 py-2 border-b last:border-0">
                  <Checkbox
                    /* ⛔ IN COHORT MODE, ONLY TARGETABLE ROWS READ AS SELECTED. Ticking every
                       displayed row showed feed-only products as included in an operation that
                       cannot process them — the display asserting something the request could not
                       deliver. Latent while cohort mode is gated off, fixed anyway because the
                       opt-in path is the one a future endpoint turns on. */
                    checked={(target.isCohort && p.id_kind === idKind) || target.selectedIds.includes(p.id)}
                    /* ⛔⛔ A row whose id came from another table cannot be targeted by this endpoint.
                       Leaving it selectable let the picker emit feed ids stamped 'kbe' — the exact
                       failure ProductTarget's kind guard cannot detect, because the target arrives
                       LYING about its kind rather than holding the wrong one. */
                    disabled={target.isCohort || p.id_kind !== idKind}
                    onCheckedChange={() => target.toggle(p.id)}
                    aria-label={`Select ${p.name}`}
                  />
                  <span className="flex-1 text-sm">{p.name}</span>
                  {p.sku && <span className="text-xs font-mono select-text text-muted-foreground">{p.sku}</span>}
                  {p.is_enriched && <Badge variant="secondary" className="text-xs">enriched</Badge>}
                </label>
              ))}
              {!loading && products.length === 0 && !loadError && (
                <div className="p-4 text-sm text-muted-foreground">No products match this search.</div>
              )}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="paste" className="space-y-3">
            <Label htmlFor="paste-ids">One ID per line, or comma/space separated</Label>
            <Textarea
              id="paste-ids"
              rows={8}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder="8f14e45f-…&#10;3c59dc04-…"
            />
            <Button variant="outline" onClick={applyPaste}>Use this list</Button>
            {pasteReport && (
              // ⚠️ REPORTED, NOT SILENT. "58 selected" from 60 pasted lines, with no explanation,
              // reads as the tool losing two and the operator cannot tell which.
              <div role="status" className="text-sm space-y-1">
                <div>{pasteReport.accepted} product ID(s) selected.</div>
                {pasteReport.duplicatesRemoved > 0 && (
                  <div className="text-muted-foreground">
                    {pasteReport.duplicatesRemoved} duplicate(s) ignored.
                  </div>
                )}
                {pasteReport.rejected.length > 0 && (
                  <div className="text-destructive">
                    {pasteReport.rejected.length} entr(y/ies) were not valid IDs and were NOT
                    included: {pasteReport.rejected.slice(0, 3).join(', ')}
                    {pasteReport.rejected.length > 3 ? '…' : ''}
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {showForce && (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={force} onCheckedChange={(v) => setForce(v === true)} aria-label={forceLabel} />
            {forceLabel}
          </label>
        )}

        {confirmError && (
          <div role="alert" className="flex items-start gap-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 mt-0.5" />
            <span>{confirmError}</span>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => void confirm()} disabled={target.isEmpty || submitting}>
            {submitting ? `Starting ${actionLabel.toLowerCase()}…` : (
              <>
                {actionLabel}
                {target.size != null ? ` ${target.size.toLocaleString()}` : ''}
                {target.isCohort && target.size == null ? ' all matching' : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
